import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Router } from '@angular/router';
import { distinctUntilChanged } from 'rxjs';

import { GlobalModalService } from '../../../shared/services/global-modal.service';
import { HelpersService } from '../../../shared/services/helpers.service';
import { SignupService } from '../../../shared/services/signup.service';

import {
  ControlList,
  FormGroupControls,
  FormInput,
  RadioNoOptions,
  SelectOptions,
} from './form.model';

import { ContainerComponent } from '../../../shared/components/container/container.component';

@Component({
  selector: '[form]',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    ContainerComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
})
export class FormComponent implements OnInit, OnDestroy {
  //libraries
  formBuilder = inject(FormBuilder);
  document = inject(DOCUMENT);
  sanitizer = inject(DomSanitizer);
  helpersService = inject(HelpersService);
  modalService = inject(GlobalModalService);
  private readonly router = inject(Router);

  noRadioDisabledMap: { [value: string]: boolean } = {};


  states: SelectOptions[] = [
    {
      text: 'Alabama',
      value: 'AL',
    },
    {
      text: 'Alaska',
      value: 'AK',
    },
    {
      text: 'Arizona',
      value: 'AZ',
    },
    {
      text: 'Arkansas',
      value: 'AR',
    },
    {
      text: 'California',
      value: 'CA',
    },
    {
      text: 'Colorado',
      value: 'CO',
    },
    {
      text: 'Connecticut',
      value: 'CT',
    },
    {
      text: 'Delaware',
      value: 'DE',
    },
    {
      text: 'Florida',
      value: 'FL',
    },
    {
      text: 'Georgia',
      value: 'GA',
    },
    {
      text: 'Hawaii',
      value: 'HI',
    },
    {
      text: 'Idaho',
      value: 'ID',
    },
    {
      text: 'Illinois',
      value: 'IL',
    },
    {
      text: 'Indiana',
      value: 'IN',
    },
    {
      text: 'Iowa',
      value: 'IA',
    },
    {
      text: 'Kansas',
      value: 'KS',
    },
    {
      text: 'Kentucky',
      value: 'KY',
    },
    {
      text: 'Louisiana',
      value: 'LA',
    },
    {
      text: 'Maine',
      value: 'ME',
    },
    {
      text: 'Maryland',
      value: 'MD',
    },
    {
      text: 'Massachusetts',
      value: 'MA',
    },
    {
      text: 'Michigan',
      value: 'MI',
    },
    {
      text: 'Minnesota',
      value: 'MN',
    },
    {
      text: 'Mississippi',
      value: 'MS',
    },
    {
      text: 'Missouri',
      value: 'MO',
    },
    {
      text: 'Montana',
      value: 'MT',
    },
    {
      text: 'Nebraska',
      value: 'NE',
    },
    {
      text: 'Nevada',
      value: 'NV',
    },
    {
      text: 'New Hampshire',
      value: 'NH',
    },
    {
      text: 'New Jersey',
      value: 'NJ',
    },
    {
      text: 'New Mexico',
      value: 'NM',
    },
    {
      text: 'New York',
      value: 'NY',
    },
    {
      text: 'North Carolina',
      value: 'NC',
    },
    {
      text: 'North Dakota',
      value: 'ND',
    },
    {
      text: 'Ohio',
      value: 'OH',
    },
    {
      text: 'Oklahoma',
      value: 'OK',
    },
    {
      text: 'Oregon',
      value: 'OR',
    },
    {
      text: 'Pennsylvania',
      value: 'PA',
    },
    {
      text: 'Rhode Island',
      value: 'RI',
    },
    {
      text: 'South Carolina',
      value: 'SC',
    },
    {
      text: 'South Dakota',
      value: 'SD',
    },
    {
      text: 'Tennessee',
      value: 'TN',
    },
    {
      text: 'Texas',
      value: 'TX',
    },
    {
      text: 'Utah',
      value: 'UT',
    },
    {
      text: 'Vermont',
      value: 'VT',
    },
    {
      text: 'Virginia',
      value: 'VA',
    },
    {
      text: 'Washington',
      value: 'WA',
    },
    {
      text: 'West Virginia',
      value: 'WV',
    },
    {
      text: 'Wisconsin',
      value: 'WI',
    },
    {
      text: 'Wyoming',
      value: 'WY',
    },
  ];

  formControls: FormInput[] = [
    {
      id: 'screening',
      radioOptions: [
        {
          label: 'For Myself',
          option: 'Myself',
        },
        {
          label: 'For a loved one',
          option: 'Loved one',
        },
      ],
      type: 'radio',
      label: 'I am interested in screening...',
      isRequired: true,
      errorMessage: 'Please select an option',
      radioNoOptionsLabel: 'Because (select all that apply)',
      radioNoOptionsErrorMsg: 'Please select an option',
      radioNoOptions: [
        {
          option: 'both',
          label: 'I have T1D in my family',
          value: '00NRh00000C7QAC',
        },
        {
          option: 'both',
          label: 'I have autoimmune conditions in my family',
          value: '00NRh00000C7QAE',
        },
        {
          option: 'both',
          label: 'I have abnormal blood glucose',
          value: '00NRh00000C7QAD',
        },
        {
          option: 'both',
          label: 'None of the above',
          value: '00NRh00000C7QAF',
        },
        // {
        //   option: 'Loved one',
        //   label: 'I have at least one child with type 1 and one without',
        // },
        // {
        //   option: 'Loved one',
        //   label: 'All my children have type 1',
        // },
        // {
        //   option: 'Loved one',
        //   label: 'Someone else in my family has type 1',
        // },
        // {
        //   option: 'Loved one',
        //   label: 'No one in my family has type 1',
        // },
      ],
      colClass: 'col-12',
    },
    {
      id: 'firstname',
      label: 'First name',
      type: 'text',
      errorMessage: 'Please enter your first name.',
      placeholder: '',
      isRequired: true,
      colClass: 'col-12 col-lg-3',
      hiddenClass: 'middle-control--firstname',
    },
    {
      id: 'lastname',
      label: 'Last name',
      type: 'text',
      errorMessage: 'Please enter your last name.',
      placeholder: '',
      isRequired: true,
      colClass: 'col-12 col-lg-3',
      hiddenClass: 'middle-control--lastname',
    },
    {
      id: 'email',
      label: 'Email address',
      type: 'text',
      errorMessage: 'Please enter your email address',
      isRequired: true,
      placeholder: 'name@domain.com',
      colClass: 'col-12 4 position-relative',
    },
    {
      id: 'phone',
      label: 'Phone number',
      type: 'text',
      colClass: 'col-12 col-lg-4',
      errorMessage: 'Please enter a valid phone number.',
      isRequired: false,
      // placeholder: '( ___ ) ___ - ____',
      // mask: '(000) 000 - 0000',
    },
    {
      id: 'agreeSMS',
      checkboxText:
        'I agree to receive text communications from <strong>Type1Talk</strong> and have read and accepted the <a href="" data-sms-modal="true">SMS Terms and Conditions*<a/>',
      type: 'checkbox',
      errorMessage: 'Please agree to SMS Terms and Conditions',
      isRequired: true,
      hiddenClass: 'sms sms--hide',
    },
    {
      id: 'contactpreference',
      radioOptions: [
        {
          label: 'Email',
          option: 'Email',
        },
        {
          label: 'Text',
          option: 'Text',
        },
      ],
      type: 'radio',
      label: 'How would you prefer to be contacted?',
      isRequired: true,
      errorMessage: 'Please select an option',
      colClass: 'col-12',
    },
    {
      id: 'receiveDirect',
      checkboxText: 'I would like to receive direct mail',
      type: 'checkbox',
    },
    {
      id: 'addressOne',
      label: 'Address 1',
      type: 'text',
      errorMessage: 'Please enter an address',
      isRequired: true,
      placeholder: '',
      colClass: 'col-12 col-lg-4 ps-lg-5',
      hiddenClass: 'middle-control middle-control--hide mb-3',
    },
    {
      id: 'addressTwo',
      label: 'Address 2',
      type: 'text',
      errorMessage: 'Please enter a valid address',
      isRequired: false,
      placeholder: '',
      colClass: 'col-12 col-lg-4',
      hiddenClass: 'middle-control middle-control--hide mb-3 mb-3',
    },
    {
      id: 'city',
      label: 'City',
      type: 'text',
      isRequired: true,
      errorMessage: 'Please enter a city',
      placeholder: '',
      colClass: 'col-12 col-lg-4 ps-lg-5',
      hiddenClass: 'middle-control middle-control--hide mb-3',
    },
    {
      id: 'state',
      label: 'State',
      type: 'select',
      isRequired: true,
      errorMessage: 'Please select a state',
      selectOptions: this.states,
      placeholder: 'Select',
      colClass: 'col-4',
      hiddenClass:
        'middle-control middle-control--state middle-control--hide mb-3',
    },
    {
      id: 'zipcode',
      label: 'ZIP code',
      type: 'text',
      isRequired: true,
      maxLength: '5',
      errorMessage: 'Please enter a valid ZIP code',
      placeholder: '',
      colClass: 'col-3',
      hiddenClass:
        'middle-control middle-control--zipcode middle-control--hide mb-3',
      mask: '00000',
    },
    {
      id: 'terms',
      colClass: 'col-12',
      checkboxText:
        'I agree to <a href="https://www.sanofi.us/en/legal-notice#TermsandConditions" target="_blank" class="text-purple-200">Terms and Conditions</a> and <a href="https://www.sanofi.com/en/sanofi-us-privacy-policies" target="_blank" class="text-purple-200">Privacy Policy</a>.',
      type: 'checkbox',
      errorMessage:
        'Please agree to the Terms and Conditions and Privacy Policy',
      isRequired: true,
    },
    {
      id: 'eighteenYears',
      colClass: 'col-12 mt-3',
      checkboxText:
        'I certify that I am at least 18 years old and a US resident.',
      type: 'checkbox',
      errorMessage: 'Please confirm your age',
      isRequired: true,
    },
    {
      id: 'consent',
      colClass: 'col-12 mt-0',
      checkboxText:
        'By checking this box, I understand and consent that Sanofi US and its contracted vendors acting on its behalf to collect and use the information I provide to provide services to me as well as educational support resources from <a href="">Screenfortype1.com</a>. <strong>This box must be checked in order to receive communications about information and educational resources regarding type 1 diabetes.</strong>',
      type: 'checkbox',
      isRequired: true,
      errorMessage: 'Please confirm to receive communications',
    },
    {
      id: 'vendors',
      colClass: 'col-12 mt-0',
      checkboxText:
        'By checking this box, I understand and consent that Sanofi US shares the information I provide with its contracted vendors acting on its behalf to provide services to me as well as educational support resources from <a href="">Screenfortype1.com</a>. <strong>This box must be checked in order to receive communications about information and educational resources regarding type 1 diabetes.</strong>',
      type: 'checkbox',
      isRequired: true,
      errorMessage: 'Please confirm to receive communications',
    },
  ];

  form!: FormGroup<FormGroupControls>;

  @Output() formSubmitted = new EventEmitter<boolean>();

  invalidForm: boolean = true;

  counterList: number = -1;

  radioCheckedClass = 'radio__label--checked';

  noRadioOptionsList: RadioNoOptions[] = [];

  constructor(private signupService: SignupService) { }

  ngOnInit(): void {
    //initialize form
    this.form = this.formBuilder.nonNullable.group({
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      agreeSMS: [false],
      receiveDirect: [false],
      addressOne: ['', [Validators.required]],
      addressTwo: [''],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipcode: ['', [Validators.required, Validators.pattern(/^[0-9]*$/)]],
      screening: ['', [Validators.required]],
      contactpreference: ['', [Validators.required]],
      noradioscreening: this.formBuilder.nonNullable.control<string[]>([]),
      terms: [false, [Validators.requiredTrue]],
      eighteenYears: [false, [Validators.requiredTrue]],
      consent: [false, [Validators.requiredTrue]],
      vendors: [false, [Validators.requiredTrue]],
    });

    //reset middle form controls
    this.hiddenFormResetValidators(this.getMiddleControlList(false));
    this.showHideSMSTermsCheck();
    this.setPhoneRequired();
    this.showHideMiddleControls();
    this.showHideNoRadioControls();

    this.form.controls.noradioscreening.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((values: string[]) => {
        const noneSelected = values.includes('00NRh00000C7QAF'); // Valor de "None of the above"
        // 1. Deshabilita otras opciones si "None of the above" está seleccionada
        this.noRadioOptionsList.forEach((option) => {
          this.noRadioDisabledMap[option.value!] =
            noneSelected && option.label !== 'None of the above';
        });
      });

  }

  isNoRadioDisabled(value: string): boolean {
    return !!this.noRadioDisabledMap[value];
  }

  // submit function
  onSubmit(e: any) {
    e.preventDefault();

    // if invalid, avoid sending data
    if (this.form.invalid) {
      this.handleInvalid();
      this.invalidForm = false;
      // scroll to top
      setTimeout(() => {
        this.helpersService.scrollTo(0);
      }, 10);
      return;
    } else {
      this.signupService
        .signup(this.form.value)
        .then((data: any) => {
          console.log('>>> response', data);
          // ! salesforces does not return any response
          if (data.status == 0) {
            this.form.reset();
            this.handleValid();
            this.invalidForm = true;
          }
        })
        .catch((e) => {
          console.log(e);
        });
    }

    // scroll to top
    setTimeout(() => {
      this.helpersService.scrollTo(0);
    }, 10);
  }

  // function when form is invalid
  handleInvalid() {
    this.form.markAllAsTouched();
  }

  // function when form is valid
  handleValid() {
    // call any modal or action to be done
    // after form is submitted
    this.router.navigate(['/thank-you']);
  }

  private hiddenFormResetValidators(controlList: ControlList) {
    if (!controlList) return;

    Object.entries(controlList).forEach(([name, validators]) => {
      this.form.controls[name as keyof FormGroupControls].setValidators(
        validators
      );

      if (!validators) {
        this.form.controls[name as keyof FormGroupControls].reset();
        const inputSelect = this.document.querySelector(
          '.form-control__select__input'
        );
        inputSelect &&
          (this.document.querySelector(
            '.form-control__select__input'
          )!.innerHTML = '');
      }
      this.form.controls[
        name as keyof FormGroupControls
      ].updateValueAndValidity();
    });
  }

  private updateFormControlRequired(id: string, required: boolean): void {
    const control = this.formControls.find((ctrl) => ctrl.id === id);
    if (control) {
      control.isRequired = required;
    }
  }

  private showHideSMSTermsCheck() {
    this.form.controls.phone.valueChanges.subscribe((value) => {
      this.hideShowControlsByClass('sms', value != '' ? 'remove' : 'add');

      this.hiddenFormResetValidators(this.getSMSTermsCheckList(value != ''));
    });
  }

  private setPhoneRequired() {
    this.form.controls.contactpreference.valueChanges.subscribe((value) => {
      this.hiddenFormResetValidators(this.getPhoneControlList(value == 'Text'));
      this.updateFormControlRequired('phone', value == 'Text');
    });
  }

  private showHideMiddleControls() {
    this.form.controls.receiveDirect.valueChanges.subscribe((value) => {
      this.hideShowControlsByClass('middle-control', value ? 'remove' : 'add');

      this.hiddenFormResetValidators(this.getMiddleControlList(value));
    });
  }

  private showHideNoRadioControls() {
    this.form.controls.screening.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value) => {
        this.hideShowControlsByClass(
          'radio__no-options',
          value ? 'remove' : 'add'
        );
        this.hiddenFormResetValidators(this.getNoRadioControlList());
        this.addNoRadioControlOptions(value);
      });

    // this.form.controls.noradioscreening.valueChanges
    //   .pipe(distinctUntilChanged())
    //   .subscribe((value) => {
    //     //validate platform
    //     if (!this.helpersService.validatePlatform()) return;

    //     if (value) {
    //       //set bold to label
    //       this.helpersService.doc.querySelectorAll(
    //         '.radio__no-options label.form-check-label'
    //       );
    //       // .forEach((label) => {
    //       //   if (label?.textContent?.trim() == value)
    //       //     label?.classList.add('fw-bold');
    //       //   else label?.classList.remove('fw-bold');
    //       // });

    //       //update touched status in main radio
    //       this.form.get('screening')?.updateValueAndValidity();

    //       //enable screened check when specific option is selected
    //       // this.hideShowControlsByClass(
    //       //   'screened-check',
    //       //   value === 'No, my child does not/I do not have early-stage T1D'
    //       //     ? 'remove'
    //       //     : 'add'
    //       // );
    //       // this.hiddenFormResetValidators(
    //       //   this.getScreenedCheckControlList(
    //       //     value === 'No, my child does not/I do not have early-stage T1D'
    //       //   )
    //       // );
    //     } else {
    //       //reset hidden checkbox control shown when
    //       //specific option is selected
    //       // this.getScreenedCheckControlList(false);
    //     }
    //   });
  }

  private getNoRadioControlOptionsList(value: string) {
    const noRadioControl = this.formControls.find(
      (control) => control.id === 'screening'
    );

    return noRadioControl?.radioNoOptions?.filter(
      (option) => option.option == 'both' || option.option == value
    );
  }

  private addNoRadioControlOptions(value: string) {
    //clear options and control value
    this.noRadioOptionsList = [];
    this.form.get('noradioscreening')?.setValue([]);
    this.form.get('noradioscreening')?.markAsUntouched();

    // get all checkbox labels according to the option selected
    this.getNoRadioControlOptionsList(value)?.forEach((option) => {
      this.noRadioOptionsList.push(option);
    });
  }

  onNoRadioCheck(event: Event, option: RadioNoOptions) {
    if (!this.helpersService.validatePlatform()) return;

    const checked = (event.target as HTMLInputElement).checked;
    const control = this.form.get('noradioscreening');
    const current: string[] = control?.value || [];

    if (!control) return;

    if (checked) {
      if (option.label === 'None of the above') {
        // Al seleccionar "None of the above"
        // 1. Limpiamos otras selecciones
        // 2. Solo dejamos "None of the above"
        control.setValue([option.value || '']);

        // Forzar actualización visual en el próximo ciclo
        setTimeout(() => {
          this.form.updateValueAndValidity();
        });
      } else {
        // Quitar "None of the above" si estaba
        const updated = current.filter(val => val !== '00NRh00000C7QAF');
        control.setValue([...updated, option.value || '']);

        setTimeout(() => {
          this.form.updateValueAndValidity();
        });
      }
    } else {
      // Si se desmarca una opción
      const updated = current.filter(val => val !== option.value);
      control.setValue(updated);

      setTimeout(() => {
        this.form.updateValueAndValidity();
      });
    }

    console.log('Updated noradioscreening:', control.value);
  }



  private getMiddleControlList(addRemove = false): ControlList {
    return {
      addressOne: addRemove ? [Validators.required] : null,
      city: addRemove ? [Validators.required] : null,
      state: addRemove ? [Validators.required] : null,
      addressTwo: null,
      zipcode: addRemove
        ? [
          Validators.required,
          Validators.pattern(/^[0-9]*$/),
          Validators.maxLength(10),
        ]
        : null,
    };
  }

  private getPhoneControlList(addRemove = false): ControlList {
    return {
      phone: addRemove ? [Validators.required] : null,
    };
  }

  private getSMSTermsCheckList(addRemove = false): ControlList {
    return {
      agreeSMS: addRemove ? [Validators.requiredTrue] : null,
    };
  }

  private getNoRadioControlList(): ControlList {
    return {
      noradioscreening: [Validators.required],
    };
  }

  private hideShowControlsByClass(
    className:
      | 'sms'
      | 'middle-control'
      | 'radio__no-options'
      | 'screened-check',
    action: 'add' | 'remove'
  ) {
    const elements = Array.from(
      this.document.querySelectorAll(`.${className}`)
    );

    if (elements.length <= 0) return;

    elements.forEach((el) => {
      el.classList[action](`${className}--hide`);

      //remove bold to label
      if (className == 'radio__no-options') {
        el.querySelectorAll('label.form-check-label').forEach((label) =>
          label?.classList.remove('fw-bold')
        );
      }
    });
  }

  onCheckboxTextClick(e: Event) {
    //validate platform
    if (!this.helpersService.validatePlatform()) return;

    //validate if anchor is clicked
    const anchor = (e?.target as HTMLElement)?.closest('a');

    if (anchor) {
      //validate if anchor has data modal attribute
      if (anchor.dataset['smsModal']) {
        e.preventDefault();
        this.modalService.openModal('smsTerms');
      }
    }
  }

  onRadioCheck(e: Event) {
    const formCheck: HTMLElement | null = (e?.target as HTMLElement)?.closest(
      '.form-check'
    );
    const formRadioWrapper: HTMLElement | null = (
      e?.target as HTMLElement
    )?.closest('.radio__wrapper');

    const isRadioNoOption = (e?.target as HTMLElement)?.closest(
      '.radio__no-options'
    );

    if (!formCheck || !formRadioWrapper || isRadioNoOption) return;

    //clear all checks
    this.cleanCheckboxes(formRadioWrapper);

    // enable check
    const radioLabel = formCheck.querySelector('.radio__label');
    radioLabel?.classList.add(this.radioCheckedClass);
  }

  private cleanCheckboxes(wrapper?: HTMLElement) {
    //validate platform
    if (!this.helpersService.validatePlatform()) return;

    let allradioLabel = this.document.querySelectorAll('.radio__label');
    if (wrapper) {
      allradioLabel = wrapper.querySelectorAll('.radio__label');
    }

    allradioLabel.forEach((radio) =>
      radio.classList.remove(this.radioCheckedClass)
    );
  }

  sanitizeHTML(html: string) {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  handleSelect(e: any, id: string) {
    const header = this.helpersService.doc.querySelector(
      'header'
    ) as HTMLElement;

    const options: HTMLElement = this.helpersService.doc.querySelector(
      `#options--${id}`
    )!;
    const selectedDropdown: HTMLElement = e.target.closest(
      '.form-control__select'
    )!;
    const list: NodeList = options.querySelectorAll(
      '.form-control__select__menu__options li'
    );
    const input: HTMLElement = selectedDropdown.querySelector(
      '.form-control__select__input'
    )!;
    const messageAlert =
      this.helpersService.doc.querySelector('#alert-message')!;

    if (e.target.closest('.form-control__select__arrow')) {
      if (selectedDropdown.classList.contains('form-control__select--open')) {
        selectedDropdown.classList.remove('form-control__select--open');
        selectedDropdown.setAttribute('aria-expanded', 'false');
        this.counterList = -1;
      } else {
        selectedDropdown.classList.add('form-control__select--open');
        selectedDropdown.setAttribute('aria-expanded', 'true');
        options.classList.add('active');
      }
    }

    if (e.key == 'ArrowDown') {
      if (this.counterList <= list.length - 2) {
        this.counterList++;
        this.resetAllElementOnList(list);
        selectedDropdown.classList.add('form-control__select--open');
        selectedDropdown.setAttribute('aria-expanded', 'true');
        (list[this.counterList] as HTMLElement).classList.add('selected');
        (list[this.counterList] as HTMLElement).setAttribute(
          'aria-selected',
          'true'
        );
        (list[this.counterList] as HTMLElement).setAttribute('role', 'alert');
      } else {
        this.counterList = list.length - 1;
      }
    }

    if (e.key == 'ArrowUp') {
      if (this.counterList >= 1) {
        this.counterList--;
        this.resetAllElementOnList(list);
        selectedDropdown.classList.add('form-control__select--open');
        selectedDropdown.setAttribute('aria-expanded', 'true');
        (list[this.counterList] as HTMLElement).classList.add('selected');
        (list[this.counterList] as HTMLElement).setAttribute(
          'aria-selected',
          'true'
        );
        (list[this.counterList] as HTMLElement).setAttribute('role', 'alert');
      } else {
        this.counterList = 0;
      }
    }

    //saving data accesibility way
    if (e.code == 'Enter') {
      if (selectedDropdown.classList.contains('form-control__select--open')) {
        selectedDropdown.classList.remove('form-control__select--open');
        selectedDropdown.setAttribute('aria-expanded', 'false');
        options.classList.remove('active');
      }

      const dataValue = this.searchSelected(list).getAttribute('data-value');
      const textContent = this.searchSelected(list).textContent;
      input.textContent = textContent;
      input.classList.add('form-control__select__input--selected');

      //scroll to dropdown as its big
      this.helpersService.scrollTo(
        selectedDropdown.getBoundingClientRect().top +
        this.helpersService.nativeWindow.scrollY -
        header?.offsetHeight
      );

      // announce the option selected to VoiceOver only
      if (this.counterList > -1) {
        setTimeout(() => {
          messageAlert.classList.remove('d-none');
          messageAlert.setAttribute('role', 'alert');
          messageAlert.innerHTML = `You are selected ${textContent} in ${id}`;
          setTimeout(() => {
            messageAlert.classList.add('d-none');
            messageAlert.removeAttribute('role');
          }, 500);
        }, 1500);
      }

      (
        this.form.controls[
        id as keyof FormGroupControls
        ] as FormGroupControls['state']
      ).setValue(dataValue);
      this.form.get(id)?.markAsUntouched();

      this.counterList = -1;
    }

    //saving data manually with click
    if (
      e.type == 'click' &&
      (e.target as HTMLElement).closest('.form-control__select__menu__option')
    ) {
      const dataValue = (e.target as HTMLElement).getAttribute('data-value')!;
      const textContent = (e.target as HTMLElement).textContent;
      input.textContent = textContent;
      input.classList.add('form-control__select__input--selected');
      selectedDropdown.setAttribute('aria-expanded', 'false');
      selectedDropdown.classList.remove('form-control__select--open');
      options.classList.toggle('active');
      (
        this.form.controls[
        id as keyof FormGroupControls
        ] as FormGroupControls['state']
      ).setValue(dataValue);
      this.form.get(id)?.markAsUntouched();

      //scroll to dropdown as its big
      this.helpersService.scrollTo(
        selectedDropdown.getBoundingClientRect().top +
        this.helpersService.nativeWindow.scrollY -
        header?.offsetHeight
      );
    }

    this.form.get(id)?.markAsUntouched();
  }

  resetAllElementOnList(collection: NodeList) {
    collection.forEach((element) => {
      (element as HTMLElement).classList.remove('selected');
      (element as HTMLElement).setAttribute('aria-selected', 'false');
      (element as HTMLElement).removeAttribute('role');
    });
  }

  searchSelected(collection: NodeList): any {
    let el: any;
    collection.forEach((element) => {
      if ((element as HTMLElement).classList.contains('selected')) {
        el = element;
      }
    });
    return el;
  }

  // function to reset form
  private resetForm() {
    this.form.reset();
    this.cleanCheckboxes();
  }

  // onDestroy
  ngOnDestroy() {
    this.resetForm();
  }
}
